/**
 * Horizon Pro — shared behaviour for Pro content sections (loaded on every page).
 */

/**
 * <pro-video>: wraps a Shopify-hosted <video>.
 * data-autoplay="true"  muted autoplay while visible
 * data-click="true"     click toggles play / pause
 * Optional children: [data-pro-video-sound], [data-pro-video-timeline] > span, [data-pro-video-play]
 */
class ProVideo extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;
  #userPaused = false;

  connectedCallback() {
    this.video = this.querySelector('video');
    const video = this.video;
    if (!video) return;

    video.addEventListener('play', () => this.classList.add('is-playing'));
    video.addEventListener('pause', () => this.classList.remove('is-playing'));
    video.addEventListener('ended', () => this.classList.remove('is-playing'));
    video.addEventListener('volumechange', () => this.classList.toggle('is-unmuted', !video.muted));

    if (this.dataset.autoplay === 'true') {
      video.muted = true;
      this.#observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry) return;
          if (entry.isIntersecting && !this.#userPaused) video.play().catch(() => {});
          else video.pause();
        },
        { threshold: 0.01, rootMargin: '200px 0px' }
      );
      this.#observer.observe(this);
    }

    this.addEventListener('click', this.#onClick);

    const timeline = /** @type {HTMLElement | null} */ (this.querySelector('[data-pro-video-timeline]'));
    if (timeline) {
      const bar = /** @type {HTMLElement | null} */ (timeline.firstElementChild);
      video.addEventListener('timeupdate', () => {
        if (bar && video.duration) bar.style.width = `${(video.currentTime / video.duration) * 100}%`;
      });
      timeline.addEventListener('pointerdown', (event) => {
        event.stopPropagation();
        const seek = (/** @type {PointerEvent} */ e) => {
          const rect = timeline.getBoundingClientRect();
          const ratio = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1);
          if (video.duration) video.currentTime = ratio * video.duration;
        };
        seek(event);
        timeline.setPointerCapture(event.pointerId);
        timeline.addEventListener('pointermove', seek);
        timeline.addEventListener('pointerup', () => timeline.removeEventListener('pointermove', seek), { once: true });
      });
    }
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.removeEventListener('click', this.#onClick);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const video = this.video;
    const target = event.target instanceof Element ? event.target : null;
    if (!video || !target) return;

    if (target.closest('[data-pro-video-sound]')) {
      event.preventDefault();
      video.muted = !video.muted;
      if (video.paused) video.play().catch(() => {});
      return;
    }
    if (target.closest('[data-pro-video-timeline]')) return;
    if (this.dataset.click !== 'true' && !target.closest('[data-pro-video-play]')) return;

    event.preventDefault();
    if (video.paused) {
      this.#userPaused = false;
      if (this.dataset.autoplay !== 'true') video.muted = false;
      video.play().catch(() => {
        video.muted = true;
        video.play().catch(() => {});
      });
    } else {
      this.#userPaused = true;
      video.pause();
    }
  };
}

if (!customElements.get('pro-video')) customElements.define('pro-video', ProVideo);

/**
 * <pro-ticker>: endless horizontal marquee. Clones the content group until it covers twice
 * the screen width; the speed (1–5) sets pixels per second.
 */
class ProTicker extends HTMLElement {
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    this.track = /** @type {HTMLElement | null} */ (this.querySelector('[data-pro-ticker-track]'));
    this.group = /** @type {HTMLElement | null} */ (this.querySelector('[data-pro-ticker-group]'));
    if (!this.track || !this.group) return;
    this.classList.toggle('pro-ticker--reverse', this.dataset.direction === 'reverse');
    this.#resizeObserver = new ResizeObserver(() => this.#fill());
    this.#resizeObserver.observe(this);
    this.addEventListener('touchstart', () => this.classList.add('is-touched'), { passive: true });
    this.addEventListener('touchend', () => this.classList.remove('is-touched'), { passive: true });
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
  }

  #fill() {
    const track = this.track;
    const group = this.group;
    if (!track || !group) return;
    track.querySelectorAll('[data-pro-ticker-clone]').forEach((clone) => clone.remove());

    const groupWidth = group.scrollWidth;
    if (!groupWidth) return;
    const copies = Math.max(1, Math.ceil((this.clientWidth * 2) / groupWidth));
    for (let i = 0; i < copies; i++) {
      const clone = /** @type {HTMLElement} */ (group.cloneNode(true));
      clone.setAttribute('data-pro-ticker-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('data-pro-ticker-group');
      clone.querySelectorAll('[data-shopify-editor-block]').forEach((el) => el.removeAttribute('data-shopify-editor-block'));
      track.appendChild(clone);
    }
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const distance = groupWidth + gap;
    const speed = parseFloat(this.dataset.speed || '3') || 3;
    const pixelsPerSecond = speed * 30;
    this.style.setProperty('--pro-ticker-distance', `${distance}px`);
    this.style.setProperty('--pro-ticker-duration', `${(distance / pixelsPerSecond).toFixed(2)}s`);
  }
}

if (!customElements.get('pro-ticker')) customElements.define('pro-ticker', ProTicker);

/**
 * <pro-recommendations>: loads Shopify product recommendations for the section and shows it
 * only when there are products.
 */
class ProRecommendations extends HTMLElement {
  connectedCallback() {
    const url = this.dataset.url;
    if (!url || this.dataset.loaded) return;
    this.dataset.loaded = 'true';
    const load = () =>
      fetch(url)
        .then((response) => response.text())
        .then((text) => {
          const html = new DOMParser().parseFromString(text, 'text/html');
          const fresh = html.querySelector('pro-recommendations');
          const section = this.closest('.pro-section');
          if (fresh && fresh.innerHTML.trim()) {
            this.innerHTML = fresh.innerHTML;
            section?.removeAttribute('hidden');
            window.ProIcons?.refresh();
          }
        })
        .catch((error) => console.warn('[pro] recommendations failed', error));

    if (document.readyState === 'complete') load();
    else window.addEventListener('load', load, { once: true });
  }
}

if (!customElements.get('pro-recommendations')) customElements.define('pro-recommendations', ProRecommendations);

/**
 * <pro-bundle>: Bundle deals — checkboxes, variant dropdowns, discounted prices and one Add to cart.
 */
class ProBundle extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.#onChange);
    this.addEventListener('click', this.#onClick);
    for (const item of this.items) this.#syncItem(item, false);
    this.#updateTotal();
  }

  get items() {
    return /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[data-bundle-item]')));
  }

  /** @param {HTMLElement} item */
  #data(item) {
    if (!item.proData) {
      try {
        item.proData = JSON.parse(item.querySelector('script[data-bundle-variants]')?.textContent || '{}');
      } catch {
        item.proData = { variants: [] };
      }
    }
    return item.proData;
  }

  /** @param {number} cents */
  async #money(cents) {
    const { formatMoney } = await import('@theme/money-formatting');
    return formatMoney(Math.max(0, Math.round(cents)), this.dataset.moneyFormat || '{{amount}}', this.dataset.currency || 'USD');
  }

  /** @param {HTMLElement} item */
  #selectedVariant(item) {
    const data = this.#data(item);
    const selects = /** @type {HTMLSelectElement[]} */ (Array.from(item.querySelectorAll('select[data-bundle-option]')));
    if (selects.length === 0) return data.variants.find((v) => v.id === data.selected) ?? data.variants[0];
    const values = selects.map((select) => select.value);
    return data.variants.find((v) => v.options.every((value, i) => value === values[i]));
  }

  /**
   * @param {HTMLElement} item
   * @param {boolean} [userChange]
   * @param {number} [changedIndex]
   */
  async #syncItem(item, userChange = true, changedIndex = -1) {
    const data = this.#data(item);
    const selects = /** @type {HTMLSelectElement[]} */ (Array.from(item.querySelectorAll('select[data-bundle-option]')));
    let variant = this.#selectedVariant(item);

    if (this.dataset.skipUnavailable === 'true' && selects.length > 0) {
      if (userChange && (!variant || !variant.available) && changedIndex >= 0) {
        const value = selects[changedIndex]?.value;
        const fallback = data.variants.find((v) => v.available && v.options[changedIndex] === value);
        if (fallback) {
          fallback.options.forEach((optionValue, i) => {
            if (selects[i]) selects[i].value = optionValue;
          });
          variant = fallback;
        }
      }
      // Disable values that have no available variant with the other current values
      selects.forEach((select, i) => {
        for (const option of select.options) {
          const values = selects.map((s) => s.value);
          values[i] = option.value;
          option.disabled = !data.variants.some((v) => v.available && v.options.every((val, j) => val === values[j]));
        }
      });
    }

    item.dataset.variantId = variant ? String(variant.id) : '';
    item.classList.toggle('is-unavailable', !variant || !variant.available);

    if (variant && this.dataset.updatePrices === 'true' && userChange) {
      const multiplier = parseFloat(item.dataset.multiplier || '1');
      const fixed = parseFloat(item.dataset.fixed || '0');
      const price = Math.max(0, Math.round(variant.price * multiplier - fixed));
      const compare = variant.compare > variant.price ? variant.compare : variant.price;
      item.dataset.price = String(price);
      item.dataset.compare = String(compare);
      const priceEl = item.querySelector('[data-bundle-price]');
      const compareEl = item.querySelector('[data-bundle-compare]');
      if (priceEl) priceEl.textContent = await this.#money(price);
      if (compareEl) compareEl.textContent = compare > price ? await this.#money(compare) : '';
    } else if (variant && !item.dataset.price) {
      const multiplier = parseFloat(item.dataset.multiplier || '1');
      const fixed = parseFloat(item.dataset.fixed || '0');
      item.dataset.price = String(Math.max(0, Math.round(variant.price * multiplier - fixed)));
      item.dataset.compare = String(variant.compare > variant.price ? variant.compare : variant.price);
    }

    if (variant?.image) {
      const media = this.querySelector(`[data-bundle-media="${CSS.escape(item.dataset.bundleItem || '')}"] img`);
      if (media instanceof HTMLImageElement && userChange) {
        media.removeAttribute('srcset');
        media.src = variant.image;
      }
    }
  }

  async #updateTotal() {
    let total = 0;
    let compare = 0;
    let count = 0;
    for (const item of this.items) {
      const checked = /** @type {HTMLInputElement | null} */ (item.querySelector('[data-bundle-checkbox]'))?.checked;
      const media = this.querySelector(`[data-bundle-media="${CSS.escape(item.dataset.bundleItem || '')}"]`);
      media?.classList.toggle('is-disabled', !checked);
      item.classList.toggle('is-unchecked', !checked);
      if (!checked) continue;
      count += 1;
      total += parseFloat(item.dataset.price || '0');
      compare += parseFloat(item.dataset.compare || '0');
    }
    const multiplier = parseFloat(this.dataset.sectionMultiplier || '1');
    const fixed = parseFloat(this.dataset.sectionFixed || '0');
    total = count > 0 ? Math.max(0, Math.round(total * multiplier - fixed)) : 0;

    const button = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-bundle-submit]'));
    const anyUnavailable = this.items.some(
      (item) => /** @type {HTMLInputElement | null} */ (item.querySelector('[data-bundle-checkbox]'))?.checked && item.classList.contains('is-unavailable')
    );
    if (button) button.disabled = count === 0 || anyUnavailable;

    if (this.dataset.updatePrices !== 'true') return;
    const totalEl = this.querySelector('[data-bundle-total]');
    const compareEl = this.querySelector('[data-bundle-total-compare]');
    if (totalEl) totalEl.textContent = await this.#money(total);
    if (compareEl) compareEl.textContent = compare > total ? await this.#money(compare) : '';
  }

  /** @param {Event} event */
  #onChange = async (event) => {
    const target = event.target;
    const item = target instanceof Element ? /** @type {HTMLElement | null} */ (target.closest('[data-bundle-item]')) : null;
    if (!item) return;
    if (target instanceof HTMLSelectElement) {
      await this.#syncItem(item, true, Number(target.dataset.bundleOption));
    }
    this.#updateTotal();
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-bundle-submit]') : null;
    if (!button || /** @type {HTMLButtonElement} */ (button).disabled) return;
    const items = this.items
      .filter((item) => /** @type {HTMLInputElement | null} */ (item.querySelector('[data-bundle-checkbox]'))?.checked && item.dataset.variantId)
      .map((item) => ({ id: item.dataset.variantId, quantity: 1 }));
    if (items.length === 0) return;

    const error = /** @type {HTMLElement | null} */ (this.querySelector('[data-bundle-error]'));
    button.setAttribute('aria-busy', 'true');
    import('@theme/pro-product')
      .then(({ proAddToCart }) => proAddToCart(/** @type {any} */ (items), { source: this, checkout: this.dataset.skipCart === 'true' }))
      .then(() => error?.setAttribute('hidden', ''))
      .catch((e) => {
        if (error) {
          error.textContent = e.message;
          error.removeAttribute('hidden');
        }
      })
      .finally(() => button.removeAttribute('aria-busy'));
  };
}

if (!customElements.get('pro-bundle')) customElements.define('pro-bundle', ProBundle);

/** <pro-compare-slider>: before / after image comparison driven by a range input */
class ProCompareSlider extends HTMLElement {
  connectedCallback() {
    const range = /** @type {HTMLInputElement | null} */ (this.querySelector('input[type="range"]'));
    if (!range) return;
    const update = () => this.style.setProperty('--pro-compare-position', `${range.value}%`);
    range.addEventListener('input', update);
    update();
  }
}

if (!customElements.get('pro-compare-slider')) customElements.define('pro-compare-slider', ProCompareSlider);

/**
 * <pro-contact-form>: shows the custom error message of required fields before the form is sent.
 */
class ProContactForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.form?.addEventListener('submit', this.#onSubmit);
    this.addEventListener('input', this.#onInput);
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.#onSubmit);
    this.removeEventListener('input', this.#onInput);
  }

  /** @param {HTMLInputElement | HTMLTextAreaElement} input */
  #isValid(input) {
    const value = input.value.trim();
    if (input.required && !value) return false;
    if (value && input instanceof HTMLInputElement && input.type === 'email') return input.checkValidity();
    if (value && input.pattern) return input.checkValidity();
    return true;
  }

  /**
   * @param {HTMLInputElement | HTMLTextAreaElement} input
   * @param {boolean} valid
   */
  #showState(input, valid) {
    const field = input.closest('[data-pro-contact-field]');
    const error = field?.querySelector('.pro-contact__field-error');
    field?.toggleAttribute('data-invalid', !valid);
    input.setAttribute('aria-invalid', String(!valid));
    if (error) error.hidden = valid;
  }

  /** @param {SubmitEvent} event */
  #onSubmit = (event) => {
    const inputs = /** @type {(HTMLInputElement | HTMLTextAreaElement)[]} */ (
      Array.from(this.querySelectorAll('.pro-contact__input'))
    );
    let firstInvalid = null;
    for (const input of inputs) {
      const valid = this.#isValid(input);
      this.#showState(input, valid);
      if (!valid && !firstInvalid) firstInvalid = input;
    }
    if (firstInvalid) {
      event.preventDefault();
      firstInvalid.focus();
    }
  };

  /** @param {Event} event */
  #onInput = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement)) return;
    if (input.closest('[data-invalid]') && this.#isValid(input)) this.#showState(input, true);
  };
}

if (!customElements.get('pro-contact-form')) customElements.define('pro-contact-form', ProContactForm);

/** <pro-copy-code data-code data-success>: copies a discount code and shows the success message on the button */
class ProCopyCode extends HTMLElement {
  #timer = 0;

  connectedCallback() {
    this.button = this.querySelector('button');
    this.label = this.querySelector('[data-copy-label]');
    this.labelHTML = this.label?.innerHTML ?? '';
    this.button?.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#onClick);
    clearTimeout(this.#timer);
  }

  #onClick = async () => {
    try {
      await navigator.clipboard.writeText(this.dataset.code || '');
    } catch {
      return;
    }
    if (!this.label || !this.dataset.success) return;
    this.label.textContent = this.dataset.success;
    clearTimeout(this.#timer);
    this.#timer = window.setTimeout(() => {
      if (this.label) this.label.innerHTML = this.labelHTML;
    }, 2000);
  };
}

if (!customElements.get('pro-copy-code')) customElements.define('pro-copy-code', ProCopyCode);

/* Product card swatches: show the image of the chosen value and link the card to that variant */
document.addEventListener('click', (event) => {
  const swatch = event.target instanceof Element ? event.target.closest('.pro-card__swatch') : null;
  const card = swatch?.closest('.pro-card');
  if (!swatch || !card || !(swatch instanceof HTMLElement)) return;
  card.querySelectorAll('.pro-card__swatch.is-active').forEach((el) => el.classList.remove('is-active'));
  swatch.classList.add('is-active');
  const image = /** @type {HTMLImageElement | null} */ (card.querySelector('.pro-card__img:not(.pro-card__img--second)'));
  if (image && swatch.dataset.proCardImage) {
    image.removeAttribute('srcset');
    image.src = swatch.dataset.proCardImage;
  }
  const url = swatch.dataset.proCardUrl;
  if (url) card.querySelectorAll('a.pro-card__media, a.pro-card__title').forEach((link) => link.setAttribute('href', url));
});

/**
 * <pro-facets>: filters and sorting of the Pro collection and search grids.
 * Desktop filters apply at once (page reload); the drawer applies with its button. Empty fields are left out of the URL.
 */
class ProFacets extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.#onChange);
    this.addEventListener('click', this.#onClick);
    this.addEventListener('toggle', this.#onToggle, true);
    this.querySelectorAll('form[data-pro-facets-form]').forEach((form) => form.addEventListener('submit', this.#onSubmit));
    document.addEventListener('click', this.#onOutsideClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#onOutsideClick);
  }

  get isDropdowns() {
    return this.classList.contains('pro-facets--horizontal');
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const field = event.target;
    if (!(field instanceof HTMLInputElement || field instanceof HTMLSelectElement)) return;
    const form = field.form;
    if (!form || form.id.endsWith('-drawer')) return;
    if (field.matches('[data-pro-facets-submit], [data-pro-facets-price]')) form.requestSubmit();
  };

  /** @param {SubmitEvent} event */
  #onSubmit = (event) => {
    event.preventDefault();
    const form = /** @type {HTMLFormElement} */ (event.target);
    const params = new URLSearchParams();
    for (const [key, value] of new FormData(form)) {
      if (String(value).trim() !== '') params.append(key, String(value));
    }
    const query = params.toString();
    window.location.assign(form.action.split('?')[0] + (query ? `?${query}` : ''));
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (target?.closest('[data-pro-facets-close]')) this.querySelector('[data-pro-facets-drawer]')?.removeAttribute('open');
  };

  /** Only one dropdown is open at a time in the horizontal bar */
  #onToggle = (/** @type {Event} */ event) => {
    const details = event.target;
    if (!this.isDropdowns || !(details instanceof HTMLDetailsElement) || !details.open) return;
    if (!details.closest('.pro-facets__desktop')) return;
    this.querySelectorAll('.pro-facets__desktop details[open]').forEach((other) => {
      if (other !== details) other.removeAttribute('open');
    });
  };

  /** @param {MouseEvent} event */
  #onOutsideClick = (event) => {
    if (!this.isDropdowns || (event.target instanceof Node && this.contains(event.target))) return;
    this.querySelectorAll('.pro-facets__desktop details[open]').forEach((details) => details.removeAttribute('open'));
  };
}

if (!customElements.get('pro-facets')) customElements.define('pro-facets', ProFacets);

/** <pro-share data-url data-title data-copied>: native share sheet, or copies the link */
class ProShare extends HTMLElement {
  #timer = 0;

  connectedCallback() {
    this.querySelector('button')?.addEventListener('click', this.#onClick);
  }

  #onClick = async () => {
    const url = this.dataset.url || window.location.href;
    if (navigator.share) {
      navigator.share({ url, title: this.dataset.title }).catch(() => {});
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      return;
    }
    const label = this.querySelector('[data-share-label]');
    if (!label || !this.dataset.copied) return;
    const original = label.textContent;
    label.textContent = this.dataset.copied;
    clearTimeout(this.#timer);
    this.#timer = window.setTimeout(() => (label.textContent = original), 2000);
  };
}

if (!customElements.get('pro-share')) customElements.define('pro-share', ProShare);

/** <pro-cart-items>: quantity changes on the cart page update the cart (page reload) */
class ProCartItems extends HTMLElement {
  #timer = 0;

  connectedCallback() {
    this.addEventListener('change', this.#onChange);
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches('[data-pro-cart-qty]')) return;
    clearTimeout(this.#timer);
    this.#timer = window.setTimeout(() => input.form?.submit(), 500);
  };
}

if (!customElements.get('pro-cart-items')) customElements.define('pro-cart-items', ProCartItems);

/** <pro-cart-discount data-error>: applies or removes discount codes on the cart (Cart AJAX API), then reloads */
class ProCartDiscount extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.querySelector('[data-pro-discount-input]')?.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.#apply();
      }
    });
  }

  get codes() {
    return Array.from(this.querySelectorAll('[data-pro-discount-remove]')).map((el) => /** @type {HTMLElement} */ (el).dataset.proDiscountRemove || '');
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (target?.closest('[data-pro-discount-apply]')) this.#apply();
    const remove = /** @type {HTMLElement | null} */ (target?.closest('[data-pro-discount-remove]') ?? null);
    if (remove) this.#update(this.codes.filter((code) => code !== remove.dataset.proDiscountRemove));
  };

  #apply() {
    const input = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-pro-discount-input]'));
    const code = input?.value.trim() ?? '';
    const error = /** @type {HTMLElement | null} */ (this.querySelector('[data-pro-discount-error]'));
    if (!code) {
      if (error) {
        error.textContent = this.dataset.error || '';
        error.hidden = false;
      }
      return;
    }
    this.#update([...this.codes, code]);
  }

  /** @param {string[]} codes */
  #update(codes) {
    fetch(`${window.Shopify?.routes?.root ?? '/'}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: codes.join(',') }),
    }).finally(() => window.location.reload());
  }
}

if (!customElements.get('pro-cart-discount')) customElements.define('pro-cart-discount', ProCartDiscount);
